import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Modal from '../Modal';
import SearchableDropdown from '../SearchableDropdown';
import TaskAssigneePicker from '../TaskAssigneePicker';
import { SortableTh } from '../SortableTh';
import { useAuth } from '../../context/AuthContext';
import { useTableSortState } from '../../hooks/useTableSort';
import { sortRows } from '../../utils/tableSort';
import { makeAppointmentFilter } from '../../utils/appointmentSearch';
import {
  createInstallationAppointment,
  fetchInstallationAppointments,
  hasOpenConflict,
  type InstallationAppointmentRow,
} from '../../api/installationAppointments';
import { type ClientPickerRow } from '../../api/clients';
import { useClientPickerOptions } from '../../hooks/useClientPickerOptions';
import { fetchVerifiedUsers, type VerifiedUser } from '../../api/users';

function formatAppointment(iso: string | null): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

function AppointmentsTable({
  title,
  sectionId,
  rows,
  emptyText,
  onRowClick,
  extraColumn,
}: {
  title: string;
  sectionId: string;
  rows: InstallationAppointmentRow[];
  emptyText: string;
  onRowClick: (id: number) => void;
  extraColumn?: { label: string; key: string; render: (r: InstallationAppointmentRow) => React.ReactNode };
}) {
  const headingId = `installation-section-${sectionId}`;
  const { sortKey, sortDir, toggleSort } = useTableSortState();
  const sorted = useMemo(
    () =>
      sortRows(rows, sortKey, sortDir, (r, key) => {
        switch (key) {
          case 'job_code':
            return r.job_code ?? '';
          case 'business':
            return r.business_name ?? '';
          case 'poc':
            return r.point_of_contact ?? '';
          case 'appointment':
            return r.installation_at ?? '';
          case 'owner':
            return r.created_by_username ?? '';
          case extraColumn?.key:
            return String(extraColumn?.render(r) ?? '');
          default:
            return '';
        }
      }),
    [rows, sortKey, sortDir, extraColumn]
  );
  const colCount = 6 + (extraColumn ? 1 : 0);
  return (
    <section className="events-table-section" aria-labelledby={headingId}>
      <h2 className="events-section-title" id={headingId}>
        {title} <span className="events-section-count">({rows.length})</span>
      </h2>
      <div className="events-table-wrap">
        <table className="tasks-search-results-table">
          <thead>
            <tr>
              <SortableTh label="Job code" columnKey="job_code" sortKey={sortKey} sortDir={sortDir} onSort={toggleSort} />
              <SortableTh label="Business" columnKey="business" sortKey={sortKey} sortDir={sortDir} onSort={toggleSort} />
              <SortableTh label="Point of contact" columnKey="poc" sortKey={sortKey} sortDir={sortDir} onSort={toggleSort} />
              <SortableTh label="Installation" columnKey="appointment" sortKey={sortKey} sortDir={sortDir} onSort={toggleSort} />
              <th scope="col">Installers</th>
              <SortableTh label="Created by" columnKey="owner" sortKey={sortKey} sortDir={sortDir} onSort={toggleSort} />
              {extraColumn ? (
                <SortableTh label={extraColumn.label} columnKey={extraColumn.key} sortKey={sortKey} sortDir={sortDir} onSort={toggleSort} />
              ) : null}
            </tr>
          </thead>
          <tbody>
            {sorted.length === 0 ? (
              <tr>
                <td colSpan={colCount} className="events-empty-cell">
                  {emptyText}
                </td>
              </tr>
            ) : (
              sorted.map((r) => (
                <tr key={r.id} className="events-table-row-clickable" onClick={() => onRowClick(r.id)}>
                  <td data-label="Job code">
                    {r.job_code ? <span className="job-code">{r.job_code}</span> : '—'}
                  </td>
                  <td data-label="Business">
                    {hasOpenConflict(r) ? (
                      <span className="appt-conflict-flag" title="Client reported a scheduling conflict" aria-label="Scheduling conflict">
                        ❗
                      </span>
                    ) : null}
                    {r.business_name}
                  </td>
                  <td data-label="Point of contact">{r.point_of_contact || '—'}</td>
                  <td data-label="Installation">{formatAppointment(r.installation_at)}</td>
                  <td data-label="Installers">
                    {r.assignees.length ? r.assignees.map((a) => a.username).join(', ') : '—'}
                  </td>
                  <td data-label="Created by">{r.created_by_username ?? '—'}</td>
                  {extraColumn ? <td data-label={extraColumn.label}>{extraColumn.render(r)}</td> : null}
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </section>
  );
}

type CreateSource = 'manual' | 'client';

const InstallationDeploymentTab: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();

  const [scheduled, setScheduled] = useState<InstallationAppointmentRow[]>([]);
  const [completed, setCompleted] = useState<InstallationAppointmentRow[]>([]);
  const [voided, setVoided] = useState<InstallationAppointmentRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [searchQuery, setSearchQuery] = useState('');

  const [addOpen, setAddOpen] = useState(false);
  const [source, setSource] = useState<CreateSource>('manual');
  const { clients, clientsLoading } = useClientPickerOptions();
  const [selectedClientId, setSelectedClientId] = useState<number | null>(null);

  const [businessName, setBusinessName] = useState('');
  const [businessAddress, setBusinessAddress] = useState('');
  const [pointOfContact, setPointOfContact] = useState('');
  const [pocPhone, setPocPhone] = useState('');
  const [pocEmail, setPocEmail] = useState('');
  const [installationAt, setInstallationAt] = useState('');
  const [installationEndAt, setInstallationEndAt] = useState('');
  const [multiDay, setMultiDay] = useState(false);
  const [assigneeIds, setAssigneeIds] = useState<number[]>([]);
  const [verifiedUsers, setVerifiedUsers] = useState<VerifiedUser[]>([]);
  const [addSubmitting, setAddSubmitting] = useState(false);
  const [addError, setAddError] = useState('');

  const filterRows = useMemo(
    () =>
      makeAppointmentFilter<InstallationAppointmentRow>(searchQuery, (r) => [
        r.job_code,
        r.business_name,
        r.business_address,
        r.point_of_contact,
        r.order_number,
        r.created_by_username,
        ...r.assignees.map((a) => a.username),
      ]),
    [searchQuery]
  );

  const load = useCallback(async () => {
    setLoading(true);
    setLoadError('');
    try {
      const data = await fetchInstallationAppointments();
      setScheduled(data.scheduled);
      setCompleted(data.completed);
      setVoided(data.void);
    } catch (e) {
      setLoadError(e instanceof Error ? e.message : 'Failed to load appointments.');
      setScheduled([]);
      setCompleted([]);
      setVoided([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    let cancelled = false;
    fetchVerifiedUsers()
      .then((list) => {
        if (!cancelled) setVerifiedUsers(list);
      })
      .catch(() => {
        if (!cancelled) setVerifiedUsers([]);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  // Employees with the Installation role are listed first in the picker.
  const assigneePickerUsers = useMemo(() => {
    const hasRole = (u: VerifiedUser) => (u.roles ?? []).includes('installation');
    const ordered = [...verifiedUsers].sort((a, b) => {
      const ai = hasRole(a) ? 0 : 1;
      const bi = hasRole(b) ? 0 : 1;
      if (ai !== bi) return ai - bi;
      return a.username.localeCompare(b.username);
    });
    return ordered.map((u) => ({ id: u.id, username: u.username }));
  }, [verifiedUsers]);

  const resetForm = () => {
    setSource('manual');
    setSelectedClientId(null);
    setBusinessName('');
    setBusinessAddress('');
    setPointOfContact('');
    setPocPhone('');
    setPocEmail('');
    setInstallationAt('');
    setInstallationEndAt('');
    setMultiDay(false);
    setAssigneeIds([]);
    setAddError('');
  };

  const openAdd = () => {
    resetForm();
    setAddOpen(true);
  };

  const applyClient = (client: ClientPickerRow | null) => {
    if (!client) {
      setSelectedClientId(null);
      return;
    }
    setSelectedClientId(client.id);
    setBusinessName(client.company ?? '');
    setBusinessAddress(client.site_location || client.billing_address || '');
    setPointOfContact(client.point_of_contact ?? '');
    setPocPhone(client.contact_phone ?? '');
    setPocEmail(client.contact_email ?? '');
    setAddError('');
  };

  const submitAdd = async () => {
    if (!user?.id) return;
    const name = businessName.trim();
    if (!name) {
      setAddError('Business name is required.');
      return;
    }
    if (!installationAt) {
      setAddError('Date and time of installation is required.');
      return;
    }
    if (multiDay && !installationEndAt) {
      setAddError('Enter when the installation ends, or switch back to a single day.');
      return;
    }
    if (assigneeIds.length === 0) {
      setAddError('Select at least one installer.');
      return;
    }
    setAddSubmitting(true);
    setAddError('');
    try {
      const created = await createInstallationAppointment({
        user_id: user.id,
        client_id: source === 'client' ? selectedClientId : null,
        business_name: name,
        business_address: businessAddress.trim() || null,
        point_of_contact: pointOfContact.trim() || null,
        poc_phone: pocPhone.trim() || null,
        poc_email: pocEmail.trim() || null,
        installation_at: new Date(installationAt).toISOString(),
        installation_end_at: multiDay ? new Date(installationEndAt).toISOString() : null,
        assignee_user_ids: assigneeIds,
      });
      setAddOpen(false);
      navigate(`/installation/${created.id}`);
    } catch (e) {
      setAddError(e instanceof Error ? e.message : 'Failed to create appointment.');
    } finally {
      setAddSubmitting(false);
    }
  };

  const clientOptions = useMemo(() => clients.map((c) => c.company), [clients]);
  const selectedClientDisplay = useMemo(() => {
    const c = clients.find((x) => x.id === selectedClientId);
    return c?.company ?? '';
  }, [clients, selectedClientId]);

  return (
    <>
      <div className="page-toolbar events-page-toolbar">
        <input
          type="text"
          className="page-toolbar-search"
          placeholder="Search by job code, business, order…"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          aria-label="Search installation appointments"
        />
        <button type="button" className="page-toolbar-btn page-toolbar-btn--centered" onClick={openAdd}>
          <span aria-hidden="true">+</span> Create appointment
        </button>
      </div>

      {loadError ? (
        <p className="create-order-error" role="alert">
          {loadError}
        </p>
      ) : null}

      {loading ? (
        <p className="page-subtitle">Loading appointments…</p>
      ) : (
        <>
          <AppointmentsTable
            title="Scheduled Installations"
            sectionId="scheduled"
            rows={filterRows(scheduled)}
            emptyText="No scheduled installations."
            onRowClick={(id) => navigate(`/installation/${id}`)}
          />
          <AppointmentsTable
            title="Completed Installations"
            sectionId="completed"
            rows={filterRows(completed)}
            emptyText="No completed installations."
            onRowClick={(id) => navigate(`/installation/${id}`)}
            extraColumn={{
              label: 'Completed',
              key: 'completed',
              render: (r) => formatAppointment(r.completed_at),
            }}
          />
          <AppointmentsTable
            title="Voided Installations"
            sectionId="void"
            rows={filterRows(voided)}
            emptyText="No voided installations."
            onRowClick={(id) => navigate(`/installation/${id}`)}
            extraColumn={{
              label: 'Void reason',
              key: 'void_reason',
              render: (r) => r.void_reason || '—',
            }}
          />
        </>
      )}

      <Modal isOpen={addOpen} onClose={() => setAddOpen(false)} title="Create installation appointment" wide>
        <div className="modal-body">
          <div className="form-group">
            <span className="form-label">Source</span>
            <div className="elevators-source-row">
              <label className="elevators-radio-label">
                <input
                  type="radio"
                  name="installation-source"
                  checked={source === 'manual'}
                  onChange={() => {
                    setSource('manual');
                    setSelectedClientId(null);
                  }}
                  disabled={addSubmitting}
                />
                Enter business information
              </label>
              <label className="elevators-radio-label">
                <input
                  type="radio"
                  name="installation-source"
                  checked={source === 'client'}
                  onChange={() => setSource('client')}
                  disabled={addSubmitting}
                />
                Select a client from the database
              </label>
            </div>
          </div>

          {source === 'client' ? (
            <div className="form-group">
              <label className="form-label" htmlFor="installation-client-picker">
                Client
              </label>
              <SearchableDropdown
                options={clientOptions}
                value={selectedClientDisplay}
                onChange={(display) => {
                  const c = clients.find((x) => x.company === display) ?? null;
                  applyClient(c);
                }}
                placeholder={clientsLoading ? 'Loading clients…' : 'Type to search clients…'}
                disabled={addSubmitting || clientsLoading}
                noResultsMessage="No matching client"
              />
              {selectedClientId != null ? (
                <p className="elevators-confirm-hint">
                  Review and edit the details below, then set the installation date and time to confirm.
                </p>
              ) : null}
            </div>
          ) : null}

          <div className="form-group">
            <label htmlFor="installation-business-name" className="form-label">
              Business Name
            </label>
            <input
              id="installation-business-name"
              type="text"
              className="form-input"
              value={businessName}
              onChange={(e) => {
                setBusinessName(e.target.value);
                setAddError('');
              }}
              placeholder="e.g. Grand Plaza Hotel"
              disabled={addSubmitting}
            />
          </div>
          <div className="form-group">
            <label htmlFor="installation-business-address" className="form-label">
              Business Address
            </label>
            <input
              id="installation-business-address"
              type="text"
              className="form-input"
              value={businessAddress}
              onChange={(e) => setBusinessAddress(e.target.value)}
              placeholder="Street, City, State, ZIP"
              disabled={addSubmitting}
            />
          </div>
          <div className="elevators-form-row">
            <div className="form-group">
              <label htmlFor="installation-poc" className="form-label">
                Point of Contact
              </label>
              <input
                id="installation-poc"
                type="text"
                className="form-input"
                value={pointOfContact}
                onChange={(e) => setPointOfContact(e.target.value)}
                disabled={addSubmitting}
              />
            </div>
            <div className="form-group">
              <label htmlFor="installation-poc-phone" className="form-label">
                Point of Contact's Number
              </label>
              <input
                id="installation-poc-phone"
                type="tel"
                className="form-input"
                value={pocPhone}
                onChange={(e) => setPocPhone(e.target.value)}
                disabled={addSubmitting}
              />
            </div>
          </div>
          <div className="form-group">
            <label htmlFor="installation-poc-email" className="form-label">
              Point of Contact's Email Address
            </label>
            <input
              id="installation-poc-email"
              type="email"
              className="form-input"
              value={pocEmail}
              onChange={(e) => setPocEmail(e.target.value)}
              disabled={addSubmitting}
            />
          </div>
          <div className="form-group">
            <label htmlFor="installation-at" className="form-label">
              {multiDay ? 'Installation starts' : 'Date & Time of Installation'}
            </label>
            <input
              id="installation-at"
              type="datetime-local"
              className="form-input"
              value={installationAt}
              onChange={(e) => {
                setInstallationAt(e.target.value);
                setAddError('');
              }}
              disabled={addSubmitting}
            />
          </div>
          <div className="form-group">
            <label className="elevators-radio-label">
              <input
                type="checkbox"
                checked={multiDay}
                onChange={(e) => {
                  setMultiDay(e.target.checked);
                  if (!e.target.checked) setInstallationEndAt('');
                  setAddError('');
                }}
                disabled={addSubmitting}
              />
              This installation spans a date range
            </label>
          </div>
          {multiDay ? (
            <div className="form-group">
              <label htmlFor="installation-end-at" className="form-label">
                Installation ends
              </label>
              <input
                id="installation-end-at"
                type="datetime-local"
                className="form-input"
                value={installationEndAt}
                onChange={(e) => {
                  setInstallationEndAt(e.target.value);
                  setAddError('');
                }}
                disabled={addSubmitting}
              />
            </div>
          ) : null}

          <div className="form-group">
            <span className="form-label">Installer(s)</span>
            <p className="elevators-confirm-hint" style={{ marginTop: 0, marginBottom: '0.5rem' }}>
              Employees with the Installation role are listed first. Installers are added as assignees on the appointment's task.
            </p>
            <TaskAssigneePicker
              users={assigneePickerUsers}
              value={assigneeIds}
              onChange={setAssigneeIds}
              addPlaceholder="Search employee to assign…"
            />
          </div>

          {addError ? (
            <p className="create-order-error" role="alert">
              {addError}
            </p>
          ) : null}
        </div>
        <div className="modal-actions">
          <button type="button" className="cancel-button" disabled={addSubmitting} onClick={() => setAddOpen(false)}>
            Cancel
          </button>
          <button type="button" className="update-button" disabled={addSubmitting} onClick={() => void submitAdd()}>
            {addSubmitting ? 'Creating…' : 'Confirm & create'}
          </button>
        </div>
      </Modal>
    </>
  );
};

export default InstallationDeploymentTab;
